"use client";
import { upperFirst } from "lodash";
import cn from "classnames";
import st from "@/styles/modules/typography.module.scss";
import s from "./CategoryList.module.scss";
import { Tag } from "@/components/UI/Tag";
import { useEffect, useRef, useState } from "react";
import IconArrowDown from "public/img/icons/arrow-down.svg";
import { SpriteIcon } from "@/components/UI/SpriteIcon";

type CategoryListProps = {
  title: string;
  type?: "default" | "vertical";
  className?: string;
  wrap?: boolean;
  categories: {
    name: string;
    href: string;
  }[];
};

const MAX_HEIGHT = 155;

export const CategoryList = ({
  categories,
  title,
  type = "default",
  wrap = false,
  className,
}: CategoryListProps) => {
  const [toggleBtnVisible, setToggleBtnVisible] = useState(false);
  const [isFullHeight, setFullHeight] = useState(false);

  const [currentMaxHeight, setCurrentMaxHeight] = useState(MAX_HEIGHT);

  const listRef = useRef<HTMLUListElement>(null);
  const currentMaxHeightRef = useRef(currentMaxHeight);
  currentMaxHeightRef.current = currentMaxHeight;

  const checkToggleBtn = () => {
    if (!listRef.current) return;

    const maxScrollHeight = listRef.current.scrollHeight;

    const showToggleBtn = maxScrollHeight > MAX_HEIGHT;

    setToggleBtnVisible(showToggleBtn);

    if (showToggleBtn && currentMaxHeightRef.current > MAX_HEIGHT) {
      setCurrentMaxHeight(maxScrollHeight);
    }
  };

  const handleToggleBtnClick = () => {
    if (!listRef.current) return;
    setFullHeight(!isFullHeight);
    setCurrentMaxHeight(isFullHeight ? MAX_HEIGHT : listRef.current.scrollHeight);
  };

  useEffect(() => {
    checkToggleBtn();
    window.addEventListener("resize", checkToggleBtn);

    return () => {
      window.removeEventListener("resize", checkToggleBtn);
    };
  }, []);

  return (
    <div
      className={cn(
        s.categories,
        s[`categoriesType${upperFirst(type)}`],
        {
          [s.categoriesWrap]: wrap,
          [s.fullHeight]: isFullHeight,
        },
        className
      )}
    >
      {title && <p className={cn(st.headingH4, s.categoriesTitle)}>{title}</p>}
      <div>
        <ul
          ref={listRef}
          className={s.categoriesList}
          style={{
            maxHeight: currentMaxHeight,
          }}
        >
          {categories.map(({ href, name }) => (
            <li key={href}>
              <Tag
                className={s.tag}
                link={{
                  href,
                }}
              >
                {name}
              </Tag>
            </li>
          ))}
        </ul>
        {toggleBtnVisible && (
          <button className={s.toggleButton} onClick={handleToggleBtnClick}>
            {isFullHeight ? "Hide" : "Show all"}
            <SpriteIcon icon={IconArrowDown} />
          </button>
        )}
      </div>
    </div>
  );
};
